import React from "react";
import HeroSection from "./HeroSection";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Users, ShieldCheck, MessageCircle } from "lucide-react";

/* -------------------------------- TrustStrip -------------------------------- */

function TrustStrip() {
  const Item = ({ icon: Icon, title, desc }) => (
    <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm">
      <div className="mt-0.5 rounded-xl border border-slate-200 bg-white p-2">
        <Icon className="h-5 w-5 text-blue-600" />
      </div>
      <div className="leading-tight">
        <div className="font-semibold text-slate-900">{title}</div>
        <div className="text-xs sm:text-sm text-slate-600 mt-0.5">{desc}</div>
      </div>
    </div>
  );

  return (
    <section className="bg-gradient-to-r from-slate-50 via-white to-slate-50 border-y border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 md:grid-cols-3 gap-3">
        <Item
          icon={Sparkles}
          title="Pre-launch is live"
          desc="We’re onboarding experts now and improving the platform step-by-step."
        />
        <Item
          icon={Users}
          title="Trust is built by clients"
          desc="Clients can leave reviews after sessions to help others choose confidently."
        />
        <Item
          icon={ShieldCheck}
          title="Verification comes later"
          desc="Official verification badges will be added in a future update."
        />
      </div>
    </section>
  );
}

/* ------------------------------- QuickFilters ------------------------------- */

function QuickFilters() {
  const pre = (q) => `/Coaches/Search?${q}`;
  const Chip = ({ to, children }) => (
    <Link
      to={to}
      className="px-3 py-1.5 rounded-full border border-slate-200 bg-white text-sm text-slate-700 hover:border-blue-500 hover:text-blue-600 transition"
    >
      {children}
    </Link>
  );

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-3 mb-6">
      <div className="flex flex-wrap gap-2 md:gap-3">
        <Chip to={pre("Specialty=Personal%20Training")}>Personal Training</Chip>
        <Chip to={pre("Specialty=Kickboxing")}>Kickboxing</Chip>
        <Chip to={pre("Specialty=English")}>English</Chip>
        <Chip to={pre("Specialty=Piano")}>Piano</Chip>
        <Chip to={pre("Specialty=Web%20Development")}>Web Dev</Chip>
        <Chip to={pre("City=Dubai")}>Dubai</Chip>
        <Chip to={pre("City=Abu%20Dhabi")}>Abu Dhabi</Chip>
      </div>
    </section>
  );
}

/* ------------------------- PreLaunch WOW (compact) ------------------------- */

function PreLaunchWOW() {
  return (
    <section className="pt-4 sm:pt-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm">
          {/* soft glow */}
          <div className="pointer-events-none absolute -top-20 -right-20 h-48 w-48 rounded-full bg-blue-500/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-20 h-48 w-48 rounded-full bg-indigo-500/10 blur-3xl" />

          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            {/* Left */}
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
                <Sparkles className="h-4 w-4 text-blue-600" />
                Pre-launch · Expert onboarding
              </div>

              <p className="mt-2 text-sm sm:text-base font-semibold text-slate-900 leading-snug">
                Build your presence early and start receiving booking requests.
              </p>

              <p className="mt-1 text-xs sm:text-sm text-slate-600">
                Create your coach listing for free and get discovered. Subscribe for AED 199 per month or AED 1,910.00 per year (save 20%) when you want to unlock client inquiries.
              </p>
            </div>

            {/* Right */}
            <div className="flex w-full md:w-auto">
              <Link to="/signup" className="w-full md:w-auto">
                <Button
                  className="
                    w-full md:w-auto
                    rounded-xl
                    bg-gradient-to-r from-blue-600 to-indigo-600
                    text-white
                    hover:from-blue-700 hover:to-indigo-700
                    px-6 py-5
                    text-sm sm:text-base
                    font-semibold
                    shadow
                    transition
                  "
                >
                  Create Expert Profile — Free
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


/* ----------------------------- HowItWorks ----------------------------- */

function HowItWorks() {
  const Step = ({ n, title, desc }) => (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="h-9 w-9 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold mb-3">
        {n}
      </div>
      <h3 className="font-semibold text-slate-900">{title}</h3>
      <p className="text-slate-600 text-sm mt-1">{desc}</p>
    </div>
  );

  return (
    <section className="py-10 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-4">
          How it works
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Step
            n="1"
            title="Search & compare"
            desc="Browse by specialty, location, and what you need."
          />
          <Step
            n="2"
            title="Contact the expert"
            desc="Message directly and agree on schedule & price."
          />
          <Step
            n="3"
            title="Meet & review"
            desc="After the session, clients can leave reviews to help others."
          />
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------- FAQ ---------------------------------- */

function FAQ() {
  const Q = ({ q, a }) => (
    <details className="group rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <summary className="cursor-pointer list-none font-semibold text-slate-900 flex items-center justify-between">
        {q}
        <span className="ml-3 text-blue-600 group-open:rotate-45 transition">+</span>
      </summary>
      <p className="mt-2 text-slate-600 text-sm">{a}</p>
    </details>
  );

  return (
    <section className="py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-5">
          FAQs
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Q
            q="Are experts reviewed one-by-one by PTfinderNow?"
            a="Not during pre-launch. Profiles are created by experts, and trust is built through client reviews after sessions. Official verification badges will be added later."
          />
          <Q
            q="Do you handle payments between clients and experts?"
            a="No. PTfinderNow is a discovery and contact platform. Clients and experts agree on payment directly."
          />
          <Q
            q="How do reviews work?"
            a="Clients can leave reviews after their session experience. Reviews help others choose confidently and help experts grow faster."
          />
          <Q
            q="What’s coming next?"
            a="Verification badges, stronger trust signals, and more booking/communication improvements — step-by-step."
          />
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------- Home ----------------------------------- */

export default function Home() {
  const categories = [
    {
      id: 1,
      title: "Sport & Fitness",
      img: "https://images.unsplash.com/photo-1649887974297-4be052375a67?q=80&w=1887&auto=format&fit=crop",
    },
    {
      id: 2,
      title: "Languages",
      img: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=800&q=80",
    },
    {
      id: 3,
      title: "School Subjects",
      img: "https://images.unsplash.com/photo-1513258496099-48168024aec0?auto=format&fit=crop&w=800&q=80",
    },
    {
      id: 4,
      title: "Music & Instruments",
      img: "https://images.unsplash.com/photo-1696245843980-79b69e076ffa?q=80&w=1887&auto=format&fit=crop",
    },
    {
      id: 5,
      title: "Art & Design",
      img: "https://images.unsplash.com/photo-1635183783375-98e857771351?q=80&w=1854&auto=format&fit=crop",
    },
    {
      id: 6,
      title: "Programming",
      img: "https://images.unsplash.com/photo-1605379399642-870262d3d051?q=80&w=2106&auto=format&fit=crop",
    },
    {
      id: 7,
      title: "Business & Marketing",
      img: "https://images.unsplash.com/photo-1707301280389-257dcd962509?q=80&w=2071&auto=format&fit=crop",
    },
    {
      id: 8,
      title: "Photography & Video",
      img: "https://images.unsplash.com/photo-1596483941316-9ec21ebe780e?q=80&w=1861&auto=format&fit=crop",
    },
    {
      id: 9,
      title: "Cooking & Nutrition",
      img: "https://images.unsplash.com/photo-1614627293113-e7e68163d958?q=80&w=2080&auto=format&fit=crop",
    },
    {
      id: 11,
      title: "Life Coaching & Mindset",
      img: "https://images.unsplash.com/photo-1571939831265-d710665a1ea0?q=80&w=1864&auto=format&fit=crop",
    },
    {
      id: 12,
      title: "Beauty",
      img: "https://images.unsplash.com/photo-1562322140-8baeececf3df?q=80&w=2069&auto=format&fit=crop",
    },
  ];

  const specialities = {
  1: [
    "Personal Training",
    "Boxing",
    "Kickboxing",
    "Martial Arts",
    "CrossFit",
    "Pilates",
    "Zumba",
    "Strength Training",
    "Soccer",
    "Padel",
    "Yoga",
    "Swimming",
    "Horse Riding (Equestrian)",
  ],

    2: [
      "English",
      "French",
      "Arabic",
      "Spanish",
      "German",
      "Chinese",
      "Japanese",
      "Russian",
      "Italian",
      "TOEFL/IELTS Preparation",
    ],
    3: [
      "Mathematics",
      "Physics",
      "Biology",
      "Chemistry",
      "History",
      "Geography",
      "Literature",
      "Philosophy",
      "Economics",
      "Homework Help",
    ],
    4: [
      "Guitar",
      "Piano",
      "Violin",
      "Drums",
      "Singing",
      "Music Theory",
      "DJing",
      "Music Production",
      "Dancing",
    ],
    5: [
      "Painting",
      "Drawing",
      "Graphic Design",
      "Illustration",
      "Interior Design",
      "Fashion Design",
      "3D Modeling",
    ],
    6: [
      "Web Development",
      "Mobile App Development",
      "Game Development",
      "Machine Learning",
      "Cybersecurity",
      "Data Science",
      "DevOps",
      "UI/UX Design",
    ],
    7: [
      "Accounting",
      "Marketing",
      "Finance",
      "Entrepreneurship",
      "E-commerce",
      "Business Strategy",
      "Digital Marketing",
      "Project Management",
    ],
    8: [
      "Portrait Photography",
      "Video Editing",
      "Drone Photography",
      "Product Photography",
      "YouTube Content Creation",
      "Wedding Photography",
    ],
    9: [
      "Healthy Cooking",
      "Meal Planning",
      "Baking",
      "Vegan Cooking",
      "Sports Nutrition",
      "Culinary Arts",
    ],
    11: [
      "Confidence Building",
      "Public Speaking",
      "Goal Setting",
      "Time Management",
      "Emotional Intelligence",
      "Stress Management",
      "Productivity Coaching",
    ],
    12: ["Makeup", "Hair Styling", "Skincare", "Nail Art", "Cosmetology", "Personal Styling"],
  };

  const formatQueryParam = (v) => encodeURIComponent(v).replace(/%20/g, "+");

  const CategoryCard = ({ cat }) => (
    <Link
      to={`/Coaches/Search?CategoryName=${formatQueryParam(cat.title)}`}
      className="block"
      aria-label={`Explore ${cat.title}`}
    >
      <div className="group relative aspect-[4/3] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition will-change-transform">
        <div className="absolute inset-0 animate-pulse bg-slate-100" />
        <img
          src={cat.img}
          alt={cat.title}
          loading="lazy"
          decoding="async"
          sizes="(max-width: 768px) 75vw, (max-width: 1024px) 40vw, 20vw"
          className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105"
          onLoad={(e) => (e.currentTarget.previousSibling.style.display = "none")}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent opacity-85 transition group-hover:opacity-95" />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="flex items-center justify-between">
            <span className="text-white text-sm md:text-base font-semibold drop-shadow">
              {cat.title}
            </span>
            <span className="inline-flex items-center gap-1 text-white/90 text-xs">
              Explore <ArrowRight className="h-4 w-4" />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );

  return (
    <div className="bg-white text-gray-900 relative overflow-x-hidden">
      {/* 🔥 WOW top banner */}
      <PreLaunchWOW />

      {/* HERO */}
      <div className="relative w-full mt-4 sm:mt-6">
        <HeroSection />
      </div>

      {/* Trust strip */}
      <TrustStrip />

      {/* Quick filters */}
      <QuickFilters />

      {/* Categories */}
      <section className="relative z-10 pt-8 md:pt-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3 }}
            className="text-[clamp(20px,3vw,34px)] font-bold text-gray-900 mb-5 text-center"
          >
            Explore Categories
          </motion.h2>

          {/* horizontal scroll on mobile, grid on desktop */}
          <div className="-mx-4 px-4">
            <div className="flex gap-4 overflow-x-auto pb-2 md:grid md:grid-cols-4 lg:grid-cols-5 md:gap-4 md:overflow-visible">
              {categories.map((cat) => (
                <motion.div
                  key={cat.id}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.25 }}
                  className="w-[78%] min-w-[220px] md:w-auto md:min-w-0"
                >
                  <CategoryCard cat={cat} />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Specialities */}
      <section className="py-14 md:py-18 lg:py-20 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3 }}
            className="text-[clamp(22px,3.2vw,40px)] font-extrabold text-center mb-9 md:mb-12 text-gray-900"
          >
            Explore Specialities
          </motion.h2>

          <div className="space-y-10 md:space-y-14">
            {categories.map((cat) => (
              <div key={cat.id}>
                <h3 className="text-[clamp(18px,2.6vw,26px)] font-semibold mb-4 text-gray-800">
                  {cat.title}
                </h3>
                <div className="flex flex-wrap gap-2.5 md:gap-3">
                  {specialities[cat.id]?.map((spec, idx) => (
                    <Link
                      key={idx}
                      to={`/Coaches/Search?CategoryName=${formatQueryParam(cat.title)}&Specialty=${formatQueryParam(spec)}`}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-sm text-gray-800 shadow-sm hover:border-blue-500 hover:text-blue-600 transition"
                      aria-label={`Explore ${spec} in ${cat.title}`}
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                      {spec}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <HowItWorks />

     {/* CTA for experts */}
<section className="relative overflow-hidden">
  <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 py-16 md:py-20 text-white">
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
      <motion.h2
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.35 }}
        className="text-[clamp(24px,3.5vw,44px)] font-extrabold mb-3"
      >
        Experts: build your reputation early.
      </motion.h2>

      <p className="text-[clamp(14px,2.2vw,20px)] text-slate-300 mb-8">
        Create a clean profile. Get discovered by real clients.  
        Grow naturally with client reviews and long-term visibility.
      </p>

      <Link to="/signup">
        <Button
          className="
            bg-gradient-to-r from-blue-600 to-indigo-600
            text-white
            hover:from-blue-700 hover:to-indigo-700
            px-9 md:px-12
            py-3.5 md:py-4
            text-base md:text-lg
            font-semibold
            rounded-2xl
            shadow-lg
            hover:shadow-xl
            transition-all
          "
        >
          Create Expert Profile
        </Button>
      </Link>

      <div className="mt-5 flex flex-wrap items-center justify-center gap-2 text-xs text-slate-300">
        <span className="inline-flex items-center rounded-full border border-white/15 bg-white/5 px-3 py-1">
          Pre-launch onboarding
        </span>
        <span className="inline-flex items-center rounded-full border border-white/15 bg-white/5 px-3 py-1">
          Client-based reviews
        </span>
        <span className="inline-flex items-center rounded-full border border-white/15 bg-white/5 px-3 py-1">
          Verification coming later
        </span>
      </div>
    </div>
  </div>
</section>

      {/* FAQ */}
      <FAQ />
    </div>
  );
}
